// Compares an ATLAS claims export (the "claims" spreadsheet run from ATLAS) with the billing
// processor's week, so claims made in ATLAS but not in the processor - and the reverse -
// can be listed. Until approval letters feed the portal, this is how claims such as newly
// approved preparation fees get reconciled.
//
// The export has no real headings (columns are labelled a, b, c...), so columns are found
// by what they hold rather than by name: the client is the "SURNAME, FIRST" column, the
// amount is the numeric one, the grant is "26A896120/0", and the description is the free
// text staff type, e.g. "ADJ 5 OCT 26", "BAIL 5 OCT", "CC PLEA PREP".

import { isClaimedAction, billingFeeFor } from "./billingConstants.js";
import { appearanceDate, splitClientName } from "./feeSlipMatching.js";
import { prepClaimAmount } from "./prepClaims.js";

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const DESCRIPTION_DATE = /\b(\d{1,2})\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?(?:\s+(\d{2}|\d{4}))?\b/i;
const GRANT = /^\d{2}[A-Z]\d{5,}\/\d+$/i;
const CLIENT = /^[A-Z][A-Z'’ -]+,\s*[A-Z]/;

const text = (value) => (value == null ? "" : String(value).trim());

function toDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const match = text(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!match) return null;
  // ATLAS writes claim dates month first (10/14/26); fall back to day first when that is impossible.
  let [, a, b, y] = match.map(Number);
  if (y < 100) y += 2000;
  const [month, day] = a > 12 ? [b, a] : [a, b];
  return new Date(y, month - 1, day);
}

function share(rows, column, test) {
  const values = rows.map(row => row[column]).filter(value => text(value) !== "");
  if (values.length === 0) return 0;
  return values.filter(test).length / values.length;
}

// rows: arrays of cell values (dates as Date objects where the sheet has real dates).
// Returns { claims, columns } or throws when the sheet does not look like a claims export.
export function parseAtlasClaimsRows(rows = []) {
  const body = rows.filter(row => Array.isArray(row) && row.some(cell => text(cell) !== ""));
  if (body.length === 0) throw new Error("The spreadsheet is empty.");
  const width = Math.max(...body.map(row => row.length));
  const columns = [...Array(width).keys()];
  const best = (test, exclude = []) => {
    let pick = -1;
    let score = 0.6;
    for (const column of columns) {
      if (exclude.includes(column)) continue;
      const s = share(body, column, test);
      if (s > score) { score = s; pick = column; }
    }
    return pick;
  };

  const client = best(value => CLIENT.test(text(value)));
  const grant = best(value => GRANT.test(text(value)), [client]);
  const claimDate = best(value => toDate(value) != null, [client, grant]);
  // The amount: a numeric column that looks like fees - under $50,000 and not one value
  // repeated (an id or count column). Of several, the one most often filled in wins.
  const isMoney = value => /^\$?\d[\d,]*(\.\d+)?$/.test(text(value)) && Number(text(value).replace(/[$,]/g, "")) < 50000;
  let amount = -1;
  let amountScore = 0.6;
  for (const column of columns) {
    if ([client, grant, claimDate].includes(column)) continue;
    const numbers = body.map(row => text(row[column])).filter(isMoney);
    if (numbers.length > 2 && new Set(numbers).size === 1) continue;
    const s = share(body, column, isMoney);
    if (s > amountScore) { amountScore = s; amount = column; }
  }
  // The description is the most varied remaining text column.
  const used = [client, grant, claimDate, amount];
  let description = -1;
  let variety = 0;
  for (const column of columns) {
    if (used.includes(column)) continue;
    const values = body.map(row => text(row[column])).filter(Boolean);
    const distinct = new Set(values).size;
    const looksLikeCodes = values.filter(value => /[a-z]/i.test(value) && value.length <= 60).length / Math.max(values.length, 1);
    if (looksLikeCodes > 0.6 && distinct > variety && !values.every(value => /^\d+$/.test(value))) {
      // Prefer the column whose entries carry dates or short codes, not the grant type.
      const dated = values.filter(value => DESCRIPTION_DATE.test(value) || value.length <= 20).length / values.length;
      if (dated > 0.5) { variety = distinct; description = column; }
    }
  }
  if (client === -1) throw new Error("No client column (SURNAME, First) was found. Is this the ATLAS claims export?");

  const claims = body
    .filter(row => CLIENT.test(text(row[client])))
    .map((row, index) => {
      const claimedOn = claimDate === -1 ? null : toDate(row[claimDate]);
      const descriptionText = description === -1 ? "" : text(row[description]);
      const raw = amount === -1 ? "" : text(row[amount]).replace(/[$,]/g, "");
      return {
        index,
        client: text(row[client]),
        grant: grant === -1 ? "" : text(row[grant]),
        claimedOn,
        amount: raw === "" ? null : Number(raw),
        description: descriptionText,
        appearanceOn: descriptionDate(descriptionText, claimedOn),
      };
    });
  return { claims, columns: { client, grant, claimDate, amount, description } };
}

// "ADJ 5 OCT 26" -> 5 Oct 2026. Without a year, the claim date's year is used (or the year
// before, for a December appearance claimed in January).
export function descriptionDate(description = "", claimedOn = null) {
  const match = String(description).match(DESCRIPTION_DATE);
  if (!match) return null;
  const day = Number(match[1]);
  const month = MONTHS[match[2].toLowerCase()];
  let year = match[3] ? Number(match[3]) : null;
  if (year != null && year < 100) year += 2000;
  if (year == null) {
    const base = claimedOn || new Date();
    year = base.getFullYear();
    if (month > base.getMonth() + 1) year -= 1;
  }
  const date = new Date(year, month, day);
  return date.getMonth() === month ? date : null;
}

const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const sameClient = (a, b) => {
  const left = splitClientName(a);
  const right = splitClientName(b);
  return Boolean(left?.surname && right?.surname && left.surname === right.surname
    && (!left.given || !right.given || left.given === right.given));
};

const grantKey = (value = "") => String(value).replace(/\s+/g, "").toUpperCase();

// entries/actions/fees are the processor week's, prepLines its preparation claims
// (src/lib/prepClaims.js). Returns the lists to show.
export function compareAtlasClaims(entries = [], actions = {}, fees = {}, claims = [], prepLines = []) {
  const dated = entries.map(entry => ({ entry, date: appearanceDate(entry.date) }));
  const times = dated.map(item => item.date?.getTime()).filter(Number.isFinite);
  const weekStart = times.length ? Math.min(...times) : null;
  const weekEnd = times.length ? Math.max(...times) : null;
  const usedEntries = new Set();

  const matched = [];
  const amountDiffers = [];
  const atlasNotClaimedHere = [];
  const atlasNoAppearance = [];
  const usedPrep = new Set();
  const prepMatched = [];
  const prepAmountDiffers = [];

  for (const claim of claims) {
    const candidates = claim.appearanceOn
      ? dated.filter(item => !usedEntries.has(item.entry.id) && sameDay(item.date, claim.appearanceOn) && sameClient(item.entry.client_name, claim.client))
      : [];
    // Prefer an appearance already marked claimed.
    const hit = candidates.find(item => isClaimedAction(actions[item.entry.id])) || candidates[0];
    if (!hit) {
      // Not an appearance: perhaps one of the week's prep claims - same client, and the same
      // grant when both have one.
      const prep = prepLines.find(line => !usedPrep.has(line.id)
        && sameClient(line.client, claim.client)
        && (!line.grant || !claim.grant || grantKey(line.grant) === grantKey(claim.grant)));
      if (prep) {
        usedPrep.add(prep.id);
        const amount = prepClaimAmount(prep);
        const pair = { claim, line: prep, amount };
        if (claim.amount != null && amount != null && Math.abs(claim.amount - amount) >= 0.5) prepAmountDiffers.push(pair);
        else prepMatched.push(pair);
        continue;
      }
      const inWeek = claim.appearanceOn && weekStart != null
        && claim.appearanceOn.getTime() >= weekStart && claim.appearanceOn.getTime() <= weekEnd;
      atlasNoAppearance.push({
        claim,
        reason: !claim.appearanceOn
          ? "No appearance date in the description (preparation, disbursement or similar)"
          : inWeek ? "No appearance for this client on that date in the processor" : "Appearance date is outside this week",
      });
      continue;
    }
    usedEntries.add(hit.entry.id);
    const action = actions[hit.entry.id];
    const fee = billingFeeFor(hit.entry, action, fees[hit.entry.id]);
    const pair = { claim, entry: hit.entry, action: action || "", fee };
    if (!isClaimedAction(action)) atlasNotClaimedHere.push(pair);
    else if (claim.amount != null && fee != null && Math.abs(claim.amount - fee) >= 0.5) amountDiffers.push(pair);
    else matched.push(pair);
  }

  const processorOnly = entries
    .filter(entry => isClaimedAction(actions[entry.id]) && !usedEntries.has(entry.id))
    .map(entry => ({ entry, action: actions[entry.id], fee: billingFeeFor(entry, actions[entry.id], fees[entry.id]) }));

  const prepOnly = prepLines
    .filter(line => !usedPrep.has(line.id))
    .map(line => ({ line, amount: prepClaimAmount(line) }));

  return { matched, amountDiffers, atlasNotClaimedHere, atlasNoAppearance, processorOnly, prepMatched, prepAmountDiffers, prepOnly };
}
